import { useCallback, useEffect, useMemo } from "react";
import { useParams } from "react-router";

import { MasterDetailLayout } from "common/components";
import { getPageId } from "common/utilities";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { fetchClusterDigDeeperItems } from "domains/clusters/actions";
import { createDigDeeperSelector } from "domains/clusters/selectors";
import { KnowledgeDetail } from "domains/publication/components/KnowledgeDetail";
import { KnowledgeList } from "domains/publication/components/KnowledgeList";
import { createKnowledgesSelector, getKnowledgeById } from "entities/knowledge/selectors";

export const DigDeeperPage = () => {
  const dispatch = useDispatch();
  const { clusterId, id } = useParams();
  const translate = useTranslate();

  const digDeeper = useSelector(createDigDeeperSelector(clusterId));

  const knowledgesSelector = useMemo(() => createKnowledgesSelector(digDeeper?.ids ?? []), [digDeeper?.ids]);

  const items = useSelector(knowledgesSelector);

  const knowledgeId = getPageId(id, items);
  const knowledge = useSelector(getKnowledgeById(knowledgeId));

  const hasNextPage = digDeeper?.hasNextPage ?? false;
  const isPending = digDeeper?.isPending ?? false;
  const offset = digDeeper?.offset ?? 0;

  useEffect(() => {
    if (clusterId) {
      dispatch(
        fetchClusterDigDeeperItems({
          id: clusterId,
          offset: 0,
        }),
      );
    }
  }, [dispatch, clusterId]);

  const loadNextPage = useCallback(() => {
    if (clusterId) {
      dispatch(
        fetchClusterDigDeeperItems({
          id: clusterId,
          offset: offset + 1,
        }),
      );
    }
  }, [dispatch, clusterId, offset]);

  if (!knowledge) {
    return null;
  }

  const getToLink = (itemId: string) => `/monitor/emerging-themes/${clusterId}/dig-deeper/${itemId}`;

  const list = (
    <KnowledgeList
      items={items}
      isPending={isPending}
      hasNextPage={hasNextPage}
      loadNextPage={loadNextPage}
      getToLink={getToLink}
      selectedId={id}
      trackPage="Cluster detail"
    />
  );

  const shareLink = `${import.meta.env.VITE_WEBSITE_URL}${getToLink(knowledge.id)}`;

  return (
    <MasterDetailLayout
      listTitle={translate("app.cluster.dig_deeper")}
      listComponent={list}
      listBackNavigation={{
        label: translate("app.monitor.emerging_theme"),
        path: `/monitor/emerging-themes/${clusterId}`,
      }}
      detailBackNavigation={{
        label: translate("app.monitor.emerging_theme"),
        path: `/monitor/emerging-themes/${clusterId}`,
      }}
    >
      <KnowledgeDetail
        id={knowledgeId}
        knowledge={knowledge}
        shareLink={shareLink}
        topicBack={{ term: "app.cluster.dig_deeper" }}
      />
    </MasterDetailLayout>
  );
};
